/**
 * Keyboard containment for a modal dialog.
 *
 * ## Why
 *
 * Every sheet in the donor bundle declares `aria-modal="true"`. That attribute is a promise: it
 * tells assistive technology that nothing outside this dialog is available. Measured in a browser,
 * none of the sheets kept it. Opening the payment sheet left focus on `<body>`; the first Tab
 * landed on the Menu button *behind the scrim*, and the next few walked the amount buttons and the
 * Give button — all under a dimmed overlay, with the focus ring invisible or clipped.
 *
 * Two separate failures came out of that, and both matter on the money path:
 *
 *   - A donor who opened the payment sheet with a keyboard had to Tab through the entire checkout
 *     before reaching the sheet they had just opened.
 *   - The **Give** button stayed focusable underneath the sheet. Enter on a control a donor cannot
 *     see, on the screen where the mistake is irreversible, is the worst place in the product for
 *     focus to be somewhere other than where it appears.
 *
 * ## What it does
 *
 * On open: remembers what had focus, then moves focus into the panel — to its first focusable
 * control, or to the panel itself when it has none, so a screen reader announces the dialog rather
 * than continuing to read the page behind it.
 *
 * While open: Tab and Shift+Tab wrap at the panel's ends. Focus cannot leave.
 *
 * On close: returns focus to whatever opened the dialog. A donor who dismisses the sheet is put
 * back where they were, not at the top of the page.
 *
 * Escape is deliberately **not** handled here. Several sheets already own that key with different
 * meanings — one abandons a verification step, another only closes — and quietly taking it would
 * change behaviour the sheets have tests for.
 *
 * ## Using it
 *
 *   const panelRef = useDialogFocus<HTMLDivElement>();
 *   <div className="sheet" role="dialog" aria-modal="true">
 *     <div className="sheet__panel" ref={panelRef} tabIndex={-1}>…</div>
 *   </div>
 *
 * The ref goes on the **panel**, not the scrim: the scrim is a click-to-dismiss surface and is not
 * part of the dialog's content.
 */
import { useEffect, useRef } from "react";
/**
 * What counts as reachable by Tab.
 *
 * `:not([disabled])` and the negative-tabindex exclusion matter because a disabled Give button or
 * a programmatically-focusable panel must not become the wrap point — a trap whose ends are
 * unreachable controls reads as focus disappearing.
 */
const FOCUSABLE = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
].join(", ");
/**
 * Deliberately no layout-based visibility filter.
 *
 * The obvious one — skip anything whose box is zero — is right in a browser and wrong everywhere
 * else: jsdom has no layout engine, so every element measures zero and the filter would silently
 * remove every stop, leaving the trap holding nothing and the tests measuring a hook that does not
 * run. None of these sheets hides a control with CSS; they render it or they do not. `hidden` is
 * honoured because it is a plain attribute both realms agree on.
 */
const focusableWithin = (panel) => [...panel.querySelectorAll(FOCUSABLE)].filter((element) => !element.hasAttribute("hidden") && element.closest("[hidden]") === null);
export function useDialogFocus() {
    const panelRef = useRef(null);
    useEffect(() => {
        const panel = panelRef.current;
        if (!panel)
            return;
        // Captured before anything moves. `document.activeElement` after the first focus call is the
        // dialog itself, which would make "restore" a no-op.
        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const first = focusableWithin(panel)[0];
        if (first)
            first.focus();
        else
            panel.focus();
        const onKeyDown = (event) => {
            if (event.key !== "Tab")
                return;
            const focusable = focusableWithin(panel);
            if (focusable.length === 0) {
                // Nothing to move between; keep focus on the panel rather than letting it fall out.
                event.preventDefault();
                panel.focus();
                return;
            }
            const firstStop = focusable[0];
            const lastStop = focusable[focusable.length - 1];
            if (!firstStop || !lastStop)
                return;
            // Focus outside the panel is the case that matters most: it means something moved focus out
            // from under us, and the next Tab must come back rather than continue through the page.
            const active = document.activeElement;
            const inside = active instanceof Node && panel.contains(active);
            if (event.shiftKey && (!inside || active === firstStop)) {
                event.preventDefault();
                lastStop.focus();
                return;
            }
            if (!event.shiftKey && (!inside || active === lastStop)) {
                event.preventDefault();
                firstStop.focus();
            }
        };
        // On the document, not the panel: once focus has escaped, a panel-scoped listener never fires
        // again and the trap is gone for the rest of the dialog's life.
        document.addEventListener("keydown", onKeyDown, true);
        return () => {
            document.removeEventListener("keydown", onKeyDown, true);
            // `isConnected` because a sheet that closed by navigating away has no page to return to, and
            // focusing a detached node throws away the browser's own default placement.
            if (opener?.isConnected)
                opener.focus();
        };
    }, []);
    return panelRef;
}
//# sourceMappingURL=use-dialog-focus.js.map